import React from "react";
import { Facebook } from "@mui/icons-material";
import { Box, Grid, MenuItem } from "@mui/material";
import CustomTextField from "../../../../../components/ui/Textfield";
import { RegistryPage } from "../../registry/sitePageRegistry";
import { SEOForm } from "../../types";
import { resolveTemplateForPreview } from "../../utils/templateTokens";
import SectionCard from "./SectionCard";
import TemplateInput from "./TemplateInput";

const FALLBACK_OG_IMAGE_URL = "https://i.postimg.cc/qqFRW3Cw/download-(15).jpg";

interface OpenGraphCardProps {
  form: SEOForm;
  onChange: <K extends keyof SEOForm>(field: K, value: SEOForm[K]) => void;
  pageDefinition?: RegistryPage;
}

const OpenGraphCard: React.FC<OpenGraphCardProps> = ({
  form,
  onChange,
  pageDefinition,
}) => {
  const variables = pageDefinition?.isDynamic
    ? (pageDefinition.variables ?? [])
    : [];
  const resolvedOgImageUrl = resolveTemplateForPreview(
    form.ogImageUrl,
    variables,
  ) || FALLBACK_OG_IMAGE_URL;
  const canPreviewOgImage = !resolvedOgImageUrl.includes("{{");

  return (
    <SectionCard
      icon={<Facebook color="primary" />}
      title="Open Graph (Facebook)"
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <TemplateInput
            label="OG Title"
            value={form.ogTitle}
            onChange={(next) => onChange("ogTitle", next)}
            variables={variables}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <CustomTextField
            select
            label="OG Type"
            value={form.ogType}
            onChange={(event) => onChange("ogType", event.target.value)}
            sx={{ mt: { md: "22px" } }}
          >
            <MenuItem value="website">Website</MenuItem>
            <MenuItem value="article">Article</MenuItem>
            <MenuItem value="product">Product</MenuItem>
            <MenuItem value="profile">Profile</MenuItem>
          </CustomTextField>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TemplateInput
            multiline
            label="OG Description"
            value={form.ogDescription}
            onChange={(next) => onChange("ogDescription", next)}
            variables={variables}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TemplateInput
            label="OG Image URL"
            value={form.ogImageUrl}
            onChange={(next) => onChange("ogImageUrl", next)}
            variables={variables}
            placeholder="Image URL (fallback used when empty)"
          />
        </Grid>
        {canPreviewOgImage && (
          <Grid size={{ xs: 12 }}>
            <Box
              component="img"
              src={resolvedOgImageUrl}
              alt="Open Graph preview"
              sx={{
                width: "100%",
                maxHeight: 220,
                objectFit: "cover",
                borderRadius: 2,
              }}
              onError={(event) => {
                (event.target as HTMLImageElement).style.display = "none";
              }}
            />
          </Grid>
        )}
      </Grid>
    </SectionCard>
  );
};

export default OpenGraphCard;
